import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
    Save, Plus, Trash2, ArrowUp, ArrowDown, 
    Settings, AlertCircle, GripVertical, Copy, RotateCcw,
    Eye, X, Star, Info
} from 'lucide-react';
import api from '../../../../api/client';
import { useTranslation } from 'react-i18next';
import CustomToastContainer from '../../../CustomToastContainer';
import { toast } from 'react-toastify';

function showQuestionnaireInsightsLink() {
    try {
        const raw = localStorage.getItem('user') || localStorage.getItem('adminUser');
        if (!raw) return false;
        const user = JSON.parse(raw);
        if (user?.is_superuser === true) return false;
        return Array.isArray(user?.admin_companies) && user.admin_companies.length > 0;
    } catch (err) {
        return false;
    }
}

const QUESTION_TYPES = [
    { value: 'text', label: 'Long Text' },
    { value: 'rating', label: 'Rating (1-5)' },
    { value: 'single_choice', label: 'Single Choice' },
    { value: 'multiple_choice', label: 'Multiple Choice' }
];

export default function QuestionnaireSettings() {
    useTranslation();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [questionnaire, setQuestionnaire] = useState(null);
    const [questions, setQuestions] = useState([]);
    
    // Original states for change tracking
    const [originalQuestionnaire, setOriginalQuestionnaire] = useState(null);
    const [originalQuestions, setOriginalQuestions] = useState([]);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewResponses, setPreviewResponses] = useState({});
    const [touched, setTouched] = useState(false);

    // Custom Confirmation Modal state
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        title: '',
        message: '',
        confirmText: '',
        confirmColor: '',
        onConfirm: null
    });

    // Super Admin Switcher state
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState('null');

    // Validation helper
    const getValidationErrors = () => {
        const errors = {};
        if (!questionnaire?.title || !questionnaire.title.trim()) {
            errors.title = "Title is required";
        }
        (questions || []).forEach((q) => {
            const qErrors = {};
            if (!q.question_text || !q.question_text.trim()) {
                qErrors.question_text = "Question text is required";
            }
            if (q.question_type === 'single_choice' || q.question_type === 'multiple_choice') {
                const choices = q.choices || [];
                if (choices.length < 2) {
                    qErrors.choices = "At least 2 choices are required";
                } else {
                    const emptyChoiceIdx = choices.findIndex(c => !c || !c.trim());
                    if (emptyChoiceIdx !== -1) {
                        qErrors.choices = "Choices cannot be empty";
                    }
                }
            }
            if (Object.keys(qErrors).length > 0) {
                errors[q.id] = qErrors;
            }
        });
        return errors;
    };

    const validationErrors = getValidationErrors();

    // Deep compare to track unsaved changes
    const hasUnsavedChanges = useMemo(() => {
        if (!questionnaire || !originalQuestionnaire) return false;

        // Compare general questionnaire settings
        if (questionnaire.title !== originalQuestionnaire.title) return true;
        if ((questionnaire.description || '') !== (originalQuestionnaire.description || '')) return true;
        if (questionnaire.is_enabled !== originalQuestionnaire.is_enabled) return true;
        if (questionnaire.is_optional !== originalQuestionnaire.is_optional) return true;

        // Compare questions
        if (questions.length !== originalQuestions.length) return true;

        for (let i = 0; i < questions.length; i++) {
            const q = questions[i];
            const origQ = originalQuestions[i];

            if (!origQ) return true;
            if (q.id !== origQ.id) return true;
            if (q.question_text !== origQ.question_text) return true;
            if (q.question_type !== origQ.question_type) return true;
            if (q.display_order !== origQ.display_order) return true;
            if (q.isNew) return true;

            const qChoices = q.choices || [];
            const origChoices = origQ.choices || [];
            if (qChoices.length !== origChoices.length) return true;
            for (let j = 0; j < qChoices.length; j++) {
                if (qChoices[j] !== origChoices[j]) return true;
            }
        }

        return false;
    }, [questionnaire, originalQuestionnaire, questions, originalQuestions]);

    // Update global change tracking property
    useEffect(() => {
        window.hasUnsavedQuestionnaireChanges = hasUnsavedChanges;
        return () => {
            window.hasUnsavedQuestionnaireChanges = false;
        };
    }, [hasUnsavedChanges]);

    // Prevent browser refresh/tab close if there are unsaved changes
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = 'You have unsaved questionnaire changes. Are you sure you want to leave?';
                return 'You have unsaved questionnaire changes. Are you sure you want to leave?';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [hasUnsavedChanges]);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('user'));
        const isSA = user?.is_superuser || user?.is_staff;
        setIsSuperAdmin(isSA);
        if (isSA) {
            fetchCompanies();
        }
        const defaultCompanyId = isSA ? 'null' : (user?.admin_companies?.[0] || 'null');
        setSelectedCompanyId(defaultCompanyId);
        fetchQuestionnaire(defaultCompanyId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchCompanies = async () => {
        try {
            const response = await api.get('/companies/');
            const companyList = response.data?.results || response.data || [];
            setCompanies(companyList);
        } catch (error) {
            console.error('Error fetching companies:', error);
        }
    };

    const fetchQuestionnaire = async (companyId) => {
        try {
            setLoading(true);
            const response = await api.get(`/feedback-questionnaires/?company=${companyId}`);
            const data = response.data?.results || response.data;
            if (data && data.length > 0) {
                const q = data[0];
                const qCopy = JSON.parse(JSON.stringify(q));
                setQuestionnaire(qCopy);
                setOriginalQuestionnaire(JSON.parse(JSON.stringify(qCopy)));
                setQuestions(qCopy.questions || []);
                setOriginalQuestions(JSON.parse(JSON.stringify(qCopy.questions || [])));
            } else {
                // If none exists, create a default one
                await createDefaultQuestionnaire(companyId);
            }
        } catch (error) {
            console.error('Error fetching questionnaire:', error);
            toast.error('Failed to load questionnaire settings');
        } finally {
            setLoading(false);
        }
    };

    const createDefaultQuestionnaire = async (companyId) => {
        try {
            const actualCompanyId = companyId === 'null' ? null : companyId;
            const response = await api.post('/feedback-questionnaires/', {
                company: actualCompanyId,
                title: 'Post-Interview Feedback',
                description: actualCompanyId 
                    ? 'We value your feedback. Please let us know how your interview experience was.' 
                    : "We'd love to hear your thoughts about your practice interview experience.",
                is_enabled: true,
                is_optional: true
            });
            const q = response.data;
            const qCopy = JSON.parse(JSON.stringify(q));
            setQuestionnaire(qCopy);
            setOriginalQuestionnaire(JSON.parse(JSON.stringify(qCopy)));
            setQuestions([]);
            setOriginalQuestions([]);
        } catch (error) {
            console.error('Error creating default questionnaire:', error);
        }
    };

    const handleCompanyChange = (companyId) => {
        setSelectedCompanyId(companyId);
        fetchQuestionnaire(companyId);
    };

    const handleQuestionnaireChange = (field, value) => {
        setQuestionnaire(prev => ({ ...prev, [field]: value }));
    };

    const handleSaveChanges = async () => {
        const errors = getValidationErrors();
        if (Object.keys(errors).length > 0) {
            setTouched(true);
            toast.error('Please fix validation errors before saving');
            return;
        }

        try {
            setSaving(true);
            
            // 1. Save general questionnaire settings
            await api.put(`/feedback-questionnaires/${questionnaire.id}/`, {
                title: questionnaire.title,
                description: questionnaire.description,
                is_enabled: questionnaire.is_enabled,
                is_optional: questionnaire.is_optional,
                company: questionnaire.company
            });

            // 2. Save all questions (both new and updated)
            for (const q of questions) {
                if (q.isNew) {
                    const { id, isNew, ...data } = q;
                    await api.post(`/feedback-questionnaires/${questionnaire.id}/questions/`, data);
                } else {
                    await api.put(`/feedback-questionnaires/${questionnaire.id}/questions/${q.id}/`, q);
                }
            }

            toast.success('Changes saved successfully');
            setTouched(false);
            await fetchQuestionnaire(selectedCompanyId); // Refresh to get real database state and IDs
        } catch (error) {
            console.error('Error saving changes:', error);
            toast.error('Failed to save changes');
        } finally {
            setSaving(false);
        }
    };

    const addQuestion = () => {
        if (questions.length >= 5) {
            toast.warning('You can add a maximum of 5 questions');
            return;
        }
        setQuestions([...questions, {
            id: `temp_${Date.now()}`,
            question_text: '',
            question_type: 'text',
            choices: [],
            display_order: questions.length,
            isNew: true
        }]);
    };

    const duplicateQuestion = (index) => {
        if (questions.length >= 5) {
            toast.warning('You can add a maximum of 5 questions');
            return;
        }
        const qToDup = questions[index];
        const dupQuestion = {
            id: `temp_${Date.now()}`,
            question_text: qToDup.question_text ? `${qToDup.question_text} (Copy)` : '',
            question_type: qToDup.question_type,
            choices: [...(qToDup.choices || [])],
            display_order: questions.length,
            isNew: true
        };
        setQuestions([...questions, dupQuestion]);
        toast.info('Question duplicated. Click Save Changes to store.');
    };

    const removeQuestion = async (index) => {
        const question = questions[index];
        if (!question.isNew) {
            try {
                await api.delete(`/feedback-questionnaires/${questionnaire.id}/questions/${question.id}/`);
            } catch (error) {
                console.error('Error deleting question:', error);
                toast.error('Failed to delete question');
                return;
            }
        }
        
        const newQuestions = [...questions];
        newQuestions.splice(index, 1);
        
        // Update display_order for remaining questions
        const updatedQuestions = newQuestions.map((q, i) => ({ ...q, display_order: i }));
        setQuestions(updatedQuestions);
        
        // Save the new order if not new
        if (!question.isNew) {
            setSaving(true);
            try {
                for (const q of updatedQuestions) {
                    await api.put(`/feedback-questionnaires/${questionnaire.id}/questions/${q.id}/`, q);
                }
                toast.success('Question removed');
                setOriginalQuestions(JSON.parse(JSON.stringify(updatedQuestions)));
            } catch (err) {
                toast.error('Failed to update question ordering');
            } finally {
                setSaving(false);
            }
        }
    };

    const moveQuestion = (index, direction) => {
        if ((direction === -1 && index === 0) || (direction === 1 && index === questions.length - 1)) return;
        
        const newQuestions = [...questions];
        const temp = newQuestions[index];
        newQuestions[index] = newQuestions[index + direction];
        newQuestions[index + direction] = temp;
        
        // Update display_order
        const updatedQuestions = newQuestions.map((q, i) => ({ ...q, display_order: i }));
        setQuestions(updatedQuestions);
    };

    const updateQuestion = (index, field, value) => {
        const newQuestions = [...questions];
        newQuestions[index][field] = value;
        if (field === 'question_type' && (value === 'single_choice' || value === 'multiple_choice')) {
            if (!newQuestions[index].choices || newQuestions[index].choices.length === 0) {
                newQuestions[index].choices = ['', ''];
            }
        }
        setQuestions(newQuestions);
    };

    const handleDiscardChanges = () => {
        setConfirmModal({
            isOpen: true,
            title: 'Discard Changes Confirmation',
            message: 'Are you sure you want to discard all unsaved changes?',
            confirmText: 'Discard',
            confirmColor: 'bg-red-600 hover:bg-red-700 text-white',
            onConfirm: () => {
                setQuestionnaire(JSON.parse(JSON.stringify(originalQuestionnaire)));
                setQuestions(JSON.parse(JSON.stringify(originalQuestions)));
                setTouched(false);
                toast.info('Changes discarded');
            }
        });
    };

    const handleRestoreDefaults = async () => {
        setConfirmModal({
            isOpen: true,
            title: 'Use Default Template',
            message: 'Are you sure you want to restore the recommended feedback questions? This will delete your current custom questions.',
            confirmText: 'Restore',
            confirmColor: 'bg-red-600 hover:bg-red-700 text-white',
            onConfirm: async () => {
                try {
                    setSaving(true);
                    const response = await api.post(`/feedback-questionnaires/${questionnaire.id}/restore-defaults/`);
                    const restoredQuestions = response.data || [];
                    setQuestions(restoredQuestions);
                    setOriginalQuestions(JSON.parse(JSON.stringify(restoredQuestions)));
                    toast.success('Recommended questions restored successfully');
                } catch (error) {
                    console.error('Error restoring recommended questions:', error);
                    toast.error('Failed to restore recommended questions');
                } finally {
                    setSaving(false);
                }
            }
        });
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!questionnaire) return null;

    return (
        <div className="w-full mx-auto p-3 sm:p-4 h-full font-sans" style={{ paddingTop: '10px' }}>
            <CustomToastContainer />
            
            <div className="bg-white rounded-xl flex flex-col h-full overflow-hidden border border-gray-200 shadow-sm">
                {/* Header section docked exactly at the top */}
                <div
                    className="px-4 py-3 text-white rounded-t-xl shadow-sm"
                    style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}
                >
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                            <h1 className="text-lg font-semibold mb-0.5 text-white">Post-Interview Feedback</h1>
                            <p className="text-xs text-blue-100">
                                Configure the feedback questionnaire shown to candidates after they complete an interview.
                            </p>
                            {showQuestionnaireInsightsLink() ? (
                                <Link
                                    to="/feedback-insights"
                                    className="mt-2 inline-flex text-xs font-semibold text-white underline underline-offset-2 hover:text-blue-50"
                                >
                                    View insights
                                </Link>
                            ) : null}
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                            <div className="relative group">
                                <button
                                    type="button"
                                    onClick={handleRestoreDefaults}
                                    disabled={saving}
                                    className="flex items-center gap-1.5 px-3 py-2 border border-white/60 hover:border-white hover:bg-white/10 text-white rounded-lg text-xs font-bold transition-all disabled:opacity-50"
                                >
                                    <RotateCcw className="w-3.5 h-3.5" />
                                    <span>Use Default Template</span>
                                    <Info className="w-3.5 h-3.5 opacity-80 ml-0.5 shrink-0" />
                                </button>
                                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 hidden group-hover:block w-72 p-3 bg-white text-gray-700 text-[11px] font-medium leading-relaxed rounded-lg shadow-xl z-[999] pointer-events-none transition-all duration-200 border border-gray-200 text-left normal-case">
                                    <p className="font-bold mb-1 text-gray-900">Restore AptiView's recommended questionnaire.</p>
                                    <ul className="space-y-0.5 text-gray-500 list-none pl-0">
                                        <li>• Includes industry-standard feedback questions</li>
                                        <li>• Designed using hiring best practices</li>
                                        <li>• Restoring will replace your current questionnaire</li>
                                    </ul>
                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-white"></div>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={handleSaveChanges}
                                disabled={saving || !hasUnsavedChanges}
                                className="flex items-center gap-2 px-5 py-2 bg-white text-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-xs font-bold shadow-sm transition-all hover:bg-white/95 active:scale-95"
                            >
                                <Save className="w-3.5 h-3.5 text-indigo-600" />
                                {saving ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Inner page content wrapper with scrolling */}
                <div className="p-6 space-y-6 overflow-y-auto flex-1">
                    {/* Super Admin Switcher */}
                    {isSuperAdmin && (
                        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
                                Questionnaire Scope (Super Admin)
                            </label>
                            <select
                                value={selectedCompanyId}
                                onChange={(e) => handleCompanyChange(e.target.value)}
                                className="w-full rounded-lg border-gray-200 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-2.5 border bg-white text-gray-800 font-medium text-sm transition-all"
                            >
                                <option value="null">Global (B2C Practice Interviews)</option>
                                {companies.map(c => (
                                    <option key={c.id} value={c.id}>
                                        Company: {c.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* General Settings */}
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                        <div className="bg-gray-50/50 border-b border-gray-100 px-6 py-4 flex items-center gap-2.5">
                            <Settings className="w-4.5 h-4.5 text-gray-500" />
                            <h2 className="text-base font-bold text-gray-900">General Settings</h2>
                        </div>

                        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8 divide-y md:divide-y-0 md:divide-x divide-gray-100">
                            {/* Left Toggles */}
                            <div className="space-y-6 pb-6 md:pb-0">
                                <div className="flex items-start justify-between">
                                    <div className="space-y-1 pr-4">
                                        <h3 className="font-semibold text-gray-900 text-sm">Enable Questionnaire</h3>
                                        <p className="text-xs text-gray-500 leading-relaxed">
                                            Show this questionnaire to candidates after interviews
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => handleQuestionnaireChange('is_enabled', !questionnaire.is_enabled)}
                                        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                            questionnaire.is_enabled ? 'bg-indigo-600' : 'bg-gray-200'
                                        }`}
                                        style={{ backgroundColor: questionnaire.is_enabled ? '#5850ec' : undefined }}
                                    >
                                        <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                            questionnaire.is_enabled ? 'translate-x-5' : 'translate-x-0'
                                        }`} />
                                    </button>
                                </div>

                                <div className="flex items-start justify-between pt-4">
                                    <div className="space-y-1 pr-4">
                                        <h3 className="font-semibold text-gray-900 text-sm">Optional Questionnaire</h3>
                                        <p className="text-xs text-gray-500 leading-relaxed">
                                            Allow candidates to skip without answering
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => handleQuestionnaireChange('is_optional', !questionnaire.is_optional)}
                                        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                            questionnaire.is_optional ? 'bg-indigo-600' : 'bg-gray-200'
                                        }`}
                                        style={{ backgroundColor: questionnaire.is_optional ? '#5850ec' : undefined }}
                                    >
                                        <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                            questionnaire.is_optional ? 'translate-x-5' : 'translate-x-0'
                                        }`} />
                                    </button>
                                </div>
                            </div>

                            {/* Right Inputs */}
                            <div className="space-y-4 pt-6 md:pt-0 md:pl-8">
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">Title</label>
                                    <input
                                        type="text"
                                        value={questionnaire.title}
                                        onChange={(e) => handleQuestionnaireChange('title', e.target.value)}
                                        className={`w-full rounded-lg border shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-2.5 text-sm transition-all ${
                                            touched && validationErrors.title ? 'border-red-400 focus:border-red-500 focus:ring-red-500 bg-red-50/10' : 'border-gray-250'
                                        }`}
                                        placeholder="e.g. Post-Interview Feedback"
                                    />
                                    {touched && validationErrors.title && (
                                        <p className="text-red-500 text-[11px] mt-1 font-semibold flex items-center gap-1">
                                            <AlertCircle className="w-3.5 h-3.5" />
                                            {validationErrors.title}
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">Description (Optional)</label>
                                    <textarea
                                        value={questionnaire.description || ''}
                                        onChange={(e) => handleQuestionnaireChange('description', e.target.value)}
                                        className="w-full rounded-lg border border-gray-250 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-2.5 text-sm min-h-[90px] transition-all"
                                        placeholder="Message shown before the questions..."
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Questions List */}
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                        <div className="border-b border-gray-100 px-6 py-4 flex items-center justify-between">
                            <h2 className="text-base font-bold text-gray-900">Questions ({questions.length})</h2>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setPreviewResponses({});
                                        setIsPreviewOpen(true);
                                    }}
                                    className="flex items-center gap-2 px-4 py-2 border border-gray-250 rounded-lg text-xs font-bold text-gray-700 bg-white hover:bg-gray-50 transition-all shadow-sm"
                                >
                                    <Eye className="w-3.5 h-3.5 text-gray-500" />
                                    Preview
                                </button>
                                <div className="relative group">
                                    <button
                                        type="button"
                                        onClick={handleRestoreDefaults}
                                        disabled={saving}
                                        className="flex items-center gap-1.5 px-3 py-2 border border-gray-250 rounded-lg text-xs font-bold text-gray-700 bg-white hover:bg-gray-50 transition-all shadow-sm disabled:opacity-50"
                                    >
                                        <RotateCcw className="w-3.5 h-3.5 text-gray-500" />
                                        <span>Use Default Template</span>
                                        <Info className="w-3.5 h-3.5 text-gray-450 ml-0.5 shrink-0" />
                                    </button>
                                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 hidden group-hover:block w-72 p-3 bg-white text-gray-700 text-[11px] font-medium leading-relaxed rounded-lg shadow-xl z-[999] pointer-events-none transition-all duration-200 border border-gray-200 text-left normal-case">
                                        <p className="font-bold mb-1 text-gray-900">Restore AptiView's recommended questionnaire.</p>
                                        <ul className="space-y-0.5 text-gray-500 list-none pl-0">
                                            <li>• Includes industry-standard feedback questions</li>
                                            <li>• Designed using hiring best practices</li>
                                            <li>• Restoring will replace your current questionnaire</li>
                                        </ul>
                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-white"></div>
                                    </div>
                                </div>
                                <button
                                    onClick={addQuestion}
                                    disabled={questions.length >= 5}
                                    className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-xs font-bold transition-all shadow-sm ${
                                        questions.length >= 5 
                                        ? 'text-gray-400 bg-gray-50 border-gray-200 cursor-not-allowed opacity-60' 
                                        : 'text-blue-700 bg-blue-50/50 border-blue-200 hover:bg-blue-100/50'
                                    }`}
                                    title={questions.length >= 5 ? 'Maximum of 5 questions allowed' : ''}
                                >
                                    <Plus className="w-3.5 h-3.5" />
                                    Add Question
                                </button>
                            </div>
                        </div>

                        <div className="p-6 space-y-4 bg-gray-50/30">
                            {questions.length === 0 ? (
                                <div className="text-center py-12 text-gray-400 bg-white rounded-xl border border-dashed border-gray-250">
                                    No questions added yet. Click "Add Question" to start.
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {questions.map((q, index) => (
                                        <div key={q.id} className="bg-white border border-gray-200 rounded-xl shadow-sm transition-all hover:shadow-md">
                                            <div className="flex items-center justify-between p-4 gap-4">
                                                {/* Drag icon */}
                                                <div className="flex items-center gap-3 shrink-0">
                                                    <div className="flex flex-col gap-1 text-gray-400 cursor-pointer p-1 rounded hover:bg-gray-50 transition-colors">
                                                        <GripVertical className="w-4 h-4" />
                                                    </div>
                                                    <div className="bg-indigo-50 text-indigo-600 rounded-lg w-8 h-8 flex items-center justify-center font-bold text-sm shrink-0" style={{ backgroundColor: '#eef2ff', color: '#4f46e5' }}>
                                                        {index + 1}
                                                    </div>
                                                </div>

                                                {/* Question text input - borderless look */}
                                                <div className="flex-1 min-w-0">
                                                    <input
                                                        type="text"
                                                        value={q.question_text}
                                                        onChange={(e) => updateQuestion(index, 'question_text', e.target.value)}
                                                        className={`w-full bg-transparent border-0 border-b focus:ring-0 text-sm font-semibold py-1 transition-all ${
                                                            touched && validationErrors[q.id]?.question_text 
                                                            ? 'border-red-400 focus:border-red-500 text-red-700 bg-red-50/5' 
                                                            : 'border-transparent hover:border-gray-200 focus:border-indigo-500 text-gray-800'
                                                        }`}
                                                        placeholder="Enter question text..."
                                                    />
                                                    {touched && validationErrors[q.id]?.question_text && (
                                                        <p className="text-red-500 text-[10px] font-semibold mt-0.5 flex items-center gap-1">
                                                            <AlertCircle className="w-3 h-3" />
                                                            {validationErrors[q.id].question_text}
                                                        </p>
                                                    )}
                                                </div>

                                                {/* Type Selector and Order buttons */}
                                                <div className="flex items-center gap-4 shrink-0">
                                                    <select
                                                        value={q.question_type}
                                                        onChange={(e) => updateQuestion(index, 'question_type', e.target.value)}
                                                        className="bg-transparent border-0 text-xs font-semibold text-gray-500 focus:ring-0 cursor-pointer hover:text-gray-700 transition-colors"
                                                    >
                                                        {QUESTION_TYPES.map(type => (
                                                            <option key={type.value} value={type.value}>{type.label}</option>
                                                        ))}
                                                    </select>

                                                    <div className="flex items-center gap-1.5">
                                                        <button 
                                                            onClick={() => moveQuestion(index, -1)}
                                                            disabled={index === 0}
                                                            className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:hover:text-gray-400 transition-colors"
                                                        >
                                                            <ArrowUp className="w-3.5 h-3.5" />
                                                        </button>
                                                        <button 
                                                            onClick={() => moveQuestion(index, 1)}
                                                            disabled={index === questions.length - 1}
                                                            className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:hover:text-gray-400 transition-colors"
                                                        >
                                                            <ArrowDown className="w-3.5 h-3.5" />
                                                        </button>
                                                    </div>

                                                    <div className="w-px h-5 bg-gray-200"></div>

                                                    {/* Action buttons */}
                                                    <div className="flex items-center gap-1">
                                                        <button 
                                                            onClick={() => duplicateQuestion(index)}
                                                            className="p-1.5 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-50 transition-all"
                                                            title="Duplicate Question"
                                                        >
                                                            <Copy className="w-4 h-4" />
                                                        </button>
                                                        <button 
                                                            onClick={() => removeQuestion(index)}
                                                            className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-all"
                                                            title="Remove Question"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Repeatable Option Editor for single/multiple selection type */}
                                            {(q.question_type === 'single_choice' || q.question_type === 'multiple_choice') && (
                                                <div className="px-4 pb-4 pl-18">
                                                    <div className={`bg-gray-50 border rounded-lg p-4 transition-colors ${
                                                        touched && validationErrors[q.id]?.choices ? 'border-red-300 bg-red-50/10' : 'border-gray-200'
                                                    }`}>
                                                        <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2">
                                                            Choices / Options (Minimum 2 required)
                                                        </label>
                                                        <div className="space-y-2.5">
                                                            {(q.choices || []).map((choice, choiceIndex) => (
                                                                <div key={choiceIndex} className="flex items-center gap-2">
                                                                    <span className="text-[11px] font-bold text-gray-400 w-5 text-right">
                                                                        {choiceIndex + 1}.
                                                                    </span>
                                                                    <input
                                                                        type="text"
                                                                        value={choice}
                                                                        onChange={(e) => {
                                                                            const newChoices = [...q.choices];
                                                                            newChoices[choiceIndex] = e.target.value;
                                                                            updateQuestion(index, 'choices', newChoices);
                                                                        }}
                                                                        className="flex-1 rounded-md border border-gray-250 text-xs p-2 focus:border-indigo-500 focus:ring-indigo-500 transition-all bg-white text-gray-800"
                                                                        placeholder={`Option ${choiceIndex + 1}`}
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            const newChoices = (q.choices || []).filter((_, i) => i !== choiceIndex);
                                                                            updateQuestion(index, 'choices', newChoices);
                                                                        }}
                                                                        className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-all shrink-0"
                                                                        title="Delete Option"
                                                                    >
                                                                        <Trash2 className="w-3.5 h-3.5" />
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                        
                                                        <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-200/60">
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    const newChoices = [...(q.choices || []), ''];
                                                                    updateQuestion(index, 'choices', newChoices);
                                                                }}
                                                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors shadow-sm"
                                                            >
                                                                <Plus className="w-3.5 h-3.5 text-indigo-600" />
                                                                Add Option
                                                            </button>
                                                            {touched && validationErrors[q.id]?.choices && (
                                                                <span className="text-red-500 text-xs font-semibold flex items-center gap-1">
                                                                    <AlertCircle className="w-3.5 h-3.5" />
                                                                    {validationErrors[q.id].choices}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}

                                    {/* Inline Add Button Container */}
                                    {questions.length < 5 ? (
                                        <div className="border border-dashed border-indigo-200 hover:border-indigo-400 bg-indigo-50/15 hover:bg-indigo-50/30 rounded-xl p-5 transition-all flex flex-col items-center justify-center gap-2 mt-4">
                                            <button
                                                type="button"
                                                onClick={addQuestion}
                                                className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-indigo-600 bg-white border border-indigo-150 rounded-lg hover:bg-indigo-50/50 transition-all shadow-sm"
                                            >
                                                <Plus className="w-3.5 h-3.5 text-indigo-600" />
                                                Add Another Question
                                            </button>
                                            <p className="text-[11px] text-gray-500 font-medium">
                                                Add more questions to continue building your questionnaire. ({questions.length}/5)
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="border border-dashed border-gray-200 bg-gray-50/50 rounded-xl p-5 flex flex-col items-center justify-center gap-1 mt-4 opacity-75">
                                            <span className="text-xs font-bold text-gray-400">Maximum of 5 questions reached</span>
                                            <p className="text-[10px] text-gray-400">Remove a question to add a new one.</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                </div>

                {/* Sticky Save Bar */}
                {hasUnsavedChanges && (
                    <div className="border-t border-amber-200 bg-amber-50/90 backdrop-blur-sm p-4 flex flex-col sm:flex-row items-center justify-between gap-4 animate-slide-up z-20">
                        <div className="flex items-center gap-3">
                            <div className="bg-amber-100 p-2.5 rounded-lg text-amber-600 shrink-0">
                                <AlertCircle className="w-5 h-5 animate-pulse" />
                            </div>
                            <div>
                                <h4 className="font-bold text-gray-900 text-sm">Unsaved Changes</h4>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    You have modified the questionnaire configuration. Save or discard your changes before leaving.
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
                            <button
                                type="button"
                                onClick={() => {
                                    setPreviewResponses({});
                                    setIsPreviewOpen(true);
                                }}
                                className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
                            >
                                <Eye className="w-3.5 h-3.5" />
                                Preview
                            </button>
                            <button
                                type="button"
                                onClick={handleDiscardChanges}
                                disabled={saving}
                                className="px-4 py-2 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors shadow-sm disabled:opacity-60"
                            >
                                Discard Changes
                            </button>
                            <button
                                type="button"
                                onClick={handleSaveChanges}
                                disabled={saving}
                                className="flex items-center gap-2 px-5 py-2 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
                                style={{ backgroundColor: '#4f46e5' }}
                            >
                                <Save className="w-3.5 h-3.5" />
                                {saving ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Preview Modal */}
            {isPreviewOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
                    <div
                        className="bg-gray-50 rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col my-8 animate-in fade-in zoom-in duration-200"
                        style={{ maxHeight: '90vh' }}
                    >
                        {/* Modal Header */}
                        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex items-center justify-between shrink-0 text-white shadow-sm">
                            <div className="flex items-center gap-3">
                                <div className="bg-white/20 p-2 rounded-lg">
                                    <Eye className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold text-white">Questionnaire Preview</h2>
                                    <p className="text-blue-100 text-xs">This is how candidates will view the questionnaire.</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setIsPreviewOpen(false)}
                                className="text-white/80 hover:text-white hover:bg-white/20 rounded-full p-1.5 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6 overflow-y-auto flex-1">
                            {/* Mock candidate interface container */}
                            <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8 shadow-sm max-w-xl mx-auto my-2">
                                <div className="border-b border-gray-150 pb-4 mb-6">
                                    <h2 className="text-xl font-bold text-gray-900">{questionnaire.title || "Feedback Questionnaire"}</h2>
                                    {questionnaire.description && (
                                        <p className="text-gray-600 text-xs mt-2 leading-relaxed">{questionnaire.description}</p>
                                    )}
                                </div>

                                <div className="space-y-6">
                                    {questions.length === 0 ? (
                                        <p className="text-sm text-gray-400 italic text-center py-4">No questions added yet.</p>
                                    ) : (
                                        [...questions]
                                            .sort((a, b) => a.display_order - b.display_order)
                                            .map((q, idx) => {
                                                const val = previewResponses[q.id];
                                                return (
                                                    <div key={q.id || idx} className="space-y-3">
                                                        <label className="block font-semibold text-gray-800 text-sm">
                                                            {idx + 1}. {q.question_text || <span className="text-red-400 italic">(Empty Question Text)</span>}
                                                        </label>

                                                        {/* Rating Input */}
                                                        {q.question_type === "rating" && (
                                                            <div className="flex gap-2 items-center">
                                                                {[1, 2, 3, 4, 5].map((star) => (
                                                                    <button
                                                                        key={star}
                                                                        type="button"
                                                                        onClick={() => setPreviewResponses(prev => ({ ...prev, [q.id]: star }))}
                                                                        className="focus:outline-none transition transform hover:scale-110"
                                                                    >
                                                                        <Star
                                                                            className={`h-6 w-6 ${
                                                                                star <= (val || 0)
                                                                                    ? "text-yellow-400 fill-yellow-400"
                                                                                    : "text-gray-300 hover:text-yellow-300"
                                                                            }`}
                                                                        />
                                                                    </button>
                                                                ))}
                                                                <span className="text-gray-500 text-xs font-semibold ml-2">
                                                                    {val ? `${val} / 5` : ""}
                                                                </span>
                                                            </div>
                                                        )}

                                                        {/* Text Input */}
                                                        {q.question_type === "text" && (
                                                            <textarea
                                                                rows={3}
                                                                value={val || ""}
                                                                onChange={(e) => setPreviewResponses(prev => ({ ...prev, [q.id]: e.target.value }))}
                                                                className="w-full rounded-xl border border-gray-300 p-3 text-xs text-gray-800 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none transition-all"
                                                                placeholder="Type feedback response here..."
                                                            />
                                                        )}

                                                        {/* Single Choice Input */}
                                                        {q.question_type === "single_choice" && (
                                                            <div className="grid gap-2">
                                                                {(!q.choices || q.choices.length === 0) ? (
                                                                    <p className="text-xs text-gray-400 italic">No choices configured.</p>
                                                                ) : (
                                                                    q.choices.map((choice, cIdx) => (
                                                                        <label
                                                                            key={cIdx}
                                                                            className={`flex items-center gap-3 p-2.5 rounded-xl border cursor-pointer transition ${
                                                                                val === choice
                                                                                    ? "bg-indigo-50/50 border-indigo-300 text-indigo-900"
                                                                                    : "bg-gray-50/50 border-gray-250 text-gray-700 hover:bg-gray-50"
                                                                            }`}
                                                                        >
                                                                            <input
                                                                                type="radio"
                                                                                name={`preview-q-${q.id}`}
                                                                                value={choice}
                                                                                checked={val === choice}
                                                                                onChange={() => setPreviewResponses(prev => ({ ...prev, [q.id]: choice }))}
                                                                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                                                                            />
                                                                            <span className="text-xs font-medium">{choice || <span className="text-red-400 italic">(Empty option)</span>}</span>
                                                                        </label>
                                                                    ))
                                                                )}
                                                            </div>
                                                        )}

                                                        {/* Multiple Choice Input */}
                                                        {q.question_type === "multiple_choice" && (
                                                            <div className="grid gap-2">
                                                                {(!q.choices || q.choices.length === 0) ? (
                                                                    <p className="text-xs text-gray-400 italic">No choices configured.</p>
                                                                ) : (
                                                                    q.choices.map((choice, cIdx) => {
                                                                        const isChecked = (val || []).includes(choice);
                                                                        return (
                                                                            <label
                                                                                key={cIdx}
                                                                                className={`flex items-center gap-3 p-2.5 rounded-xl border cursor-pointer transition ${
                                                                                    isChecked
                                                                                        ? "bg-indigo-50/50 border-indigo-300 text-indigo-900"
                                                                                        : "bg-gray-50/50 border-gray-250 text-gray-700 hover:bg-gray-50"
                                                                                }`}
                                                                            >
                                                                                <input
                                                                                    type="checkbox"
                                                                                    value={choice}
                                                                                    checked={isChecked}
                                                                                    onChange={(e) => {
                                                                                        const currentList = val || [];
                                                                                        const updatedList = e.target.checked
                                                                                            ? [...currentList, choice]
                                                                                            : currentList.filter(item => item !== choice);
                                                                                        setPreviewResponses(prev => ({ ...prev, [q.id]: updatedList }));
                                                                                    }}
                                                                                    className="h-4 w-4 text-indigo-600 rounded focus:ring-indigo-500"
                                                                                />
                                                                                <span className="text-xs font-medium">{choice || <span className="text-red-400 italic">(Empty option)</span>}</span>
                                                                            </label>
                                                                        );
                                                                    })
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })
                                    )}
                                </div>

                                <div className="flex gap-4 border-t border-gray-150 pt-5 mt-6">
                                    {questionnaire.is_optional && (
                                        <button
                                            type="button"
                                            className="flex-1 border border-gray-300 text-gray-700 rounded-xl px-4 py-2.5 font-semibold text-xs transition bg-white hover:bg-gray-50 shadow-sm"
                                            onClick={() => { toast.info("Skip button clicked (Preview Mode)"); setIsPreviewOpen(false); }}
                                        >
                                            Skip Feedback
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-4 py-2.5 font-semibold text-xs shadow-md transition-all"
                                        onClick={() => { toast.info("Submit button clicked (Preview Mode)"); setIsPreviewOpen(false); }}
                                    >
                                        Submit Feedback
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="bg-gray-100 px-6 py-3 flex justify-end shrink-0 border-t border-gray-200">
                            <button
                                onClick={() => setIsPreviewOpen(false)}
                                className="px-4 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
                            >
                                Close Preview
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Custom Confirmation Modal */}
            {confirmModal.isOpen && (
                <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[500px] overflow-hidden flex flex-col border border-gray-250 animate-slide-up">
                        {/* Modal Header */}
                        <div className="relative px-6 py-4 flex items-center justify-center border-b border-gray-150">
                            <h3 className="text-lg font-bold text-gray-900 text-center">{confirmModal.title}</h3>
                            <button
                                onClick={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
                                className="absolute right-4 text-gray-400 hover:text-gray-600 rounded-full p-1.5 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Modal Body & Footer combined */}
                        <div className="px-8 py-6 flex flex-col items-center justify-center gap-6">
                            <p className="text-sm text-gray-600 text-center leading-relaxed">
                                {confirmModal.message}
                            </p>
                            
                            <div className="flex items-center justify-center gap-4 w-full">
                                <button
                                    type="button"
                                    onClick={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
                                    className="px-6 py-2 border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 rounded-xl text-xs font-semibold transition-all shadow-sm text-center"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (confirmModal.onConfirm) confirmModal.onConfirm();
                                        setConfirmModal(prev => ({ ...prev, isOpen: false }));
                                    }}
                                    className={`px-6 py-2 text-white rounded-xl text-xs font-semibold transition-all shadow-md text-center ${confirmModal.confirmColor}`}
                                >
                                    {confirmModal.confirmText}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Styles */}
            <style>{`
              @keyframes slideUp {
                from {
                  transform: translateY(20px);
                  opacity: 0;
                }
                to {
                  transform: translateY(0);
                  opacity: 1;
                }
              }
              .animate-slide-up {
                animation: slideUp 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards;
              }
            `}</style>
        </div>
    );
}
